import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API_URL } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useAnalytics } from "../utils/useAnalytics";
import { buildPairGoogleMapsURL } from "../utils/mapUtils";
import {
  formatCurrency,
  formatMiles,
  capitalizeCity,
  capitalizeCompanyName,
  getDataSourceBadge,
  formatDate,
} from "../utils/formatting.js";
import { useTheme } from "../contexts/ThemeContext";

const getTruckTypeLabel = (code) => {
  if (!code) return code;
  const truckTypes = {
    V: "Van",
    SV: "Box Truck",
    F: "Flatbed",
    R: "Reefer",
    SD: "Step Deck",
    DD: "Double Drop",
    LB: "Lowboy",
    RGN: "Removable Gooseneck",
    TNK: "Tanker",
    AC: "Auto Carrier",
    CONT: "Container",
    DT: "Dump Truck",
    HB: "Hopper Bottom",
    PO: "Power Only",
    HS: "Hot Shot",
  };
  return truckTypes[code] || code;
};

const BackhaulResults = ({
  loadPairs: initialPairs,
  searchParams: initialParams,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { trackPairLoadSave } = useAnalytics(user);

  const [sortOption, setSortOption] = useState("score");
  const [sortedLoadPairs, setSortedLoadPairs] = useState([]);

  const loadPairs = useMemo(() => {
    const rawLoadPairs = initialPairs || [];
    return rawLoadPairs.map((pair) => ({
      ...pair,
      originCity: capitalizeCity(pair.originCity),
      destinationCity: capitalizeCity(pair.destinationCity),
      intermediateCity: capitalizeCity(pair.intermediateCity),
      returnOriginCity: capitalizeCity(pair.returnOriginCity),
      returnDestinationCity: capitalizeCity(pair.returnDestinationCity),
    }));
  }, [initialPairs]);

  const [savingPairId, setSavingPairId] = useState(null);
  const [savedPairIds, setSavedPairIds] = useState(new Set());

  const [expandedPairIds, setExpandedPairIds] = useState(new Set());

  useEffect(() => {
    let pairs = [...loadPairs];

    pairs.sort((a, b) => {
      switch (sortOption) {
        case "ratePerMile":
          return (b.average_rate || 0) - (a.average_rate || 0);
        case "totalRevenue":
          return (
            (b.outbound.rate_per_mile_est || 0) * (b.outbound.distance || 0) +
            (b.return.rate_per_mile_est || 0) * (b.return.distance || 0) -
            ((a.outbound.rate_per_mile_est || 0) * (a.outbound.distance || 0) +
              (a.return.rate_per_mile_est || 0) * (a.return.distance || 0))
          );
        case "totalMiles":
          return (b.total_miles || 0) - (a.total_miles || 0);
        case "deadheadMiles":
          return (a.deadhead_miles || 0) - (b.deadhead_miles || 0);
        case "score":
        default:
          return (b.score || 0) - (a.score || 0);
      }
    });

    setSortedLoadPairs(pairs);
  }, [loadPairs, sortOption]);

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  useEffect(() => {
    const fetchSavedLoads = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) return;

        const response = await axios.get(`${API_URL}/loads/saved`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const savedIds = new Set();
        response.data.forEach((load) => {
          if (load.is_pair && load.child) {
            const pairId = load.entry_id;
            savedIds.add(pairId);
          }
        });
        setSavedPairIds(savedIds);
      } catch (error) {
        console.error("Error fetching saved loads:", error);
      }
    };

    fetchSavedLoads();
  }, []);

  const handleSavePair = async (pair) => {
    const pairId = `${user.email}-${pair.outbound.id}-pair-${pair.return.id}`;
    setSavingPairId(pairId);

    try {
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        console.error("No authentication token found");
        return;
      }

      const outboundSaveData = {
        entry_id: `${user.email}-${pair.outbound.id}-pair-${pair.return.id}`,
        origin_city: pair.outbound.origin_city,
        origin_state: pair.outbound.origin_state,
        destination_city: pair.outbound.destination_city,
        destination_state: pair.outbound.destination_state,
        weight: pair.outbound.weight || 0,
        length: pair.outbound.length || 0,
        equipment_type:
          pair.outbound.equipment_type || pair.outbound.truck_type || "V",
        rate_per_mile_est: pair.outbound.rate_per_mile_est || 0,
        distance: pair.outbound.distance || 0,
        ship_date: pair.outbound.ship_date,
        data_source: pair.outbound.data_source || "truckstop",
        phone_number: pair.outbound.contact_phone,
        company_name: capitalizeCompanyName(pair.outbound.company_name),
        company_email: pair.outbound.company_email,
        dead_head: pair.deadhead_miles || 0,
        is_pair: true,
        child: {
          entry_id: `${user.email}-${pair.outbound.id}-pair-${pair.return.id}`,
          origin_city: pair.return.origin_city,
          origin_state: pair.return.origin_state,
          destination_city: pair.return.destination_city,
          destination_state: pair.return.destination_state,
          weight: pair.return.weight || 0,
          length: pair.return.length || 0,
          equipment_type:
            pair.return.equipment_type || pair.return.truck_type || "V",
          rate_per_mile_est: pair.return.rate_per_mile_est || 0,
          distance: pair.return.distance || 0,
          ship_date: pair.return.ship_date,
          data_source: pair.return.data_source || "truckstop",
          phone_number: pair.return.contact_phone,
          company_name: capitalizeCompanyName(pair.return.company_name),
          company_email: pair.return.company_email,
        },
      };
      const response = await axios.post(
        `${API_URL}/loads/save`,
        outboundSaveData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      trackPairLoadSave({
        entry_id: `${user.email}-${pair.outbound.id}-pair-${pair.return.id}`,
        outbound_origin: `${pair.outbound.origin_city}, ${pair.outbound.origin_state}`,
        outbound_destination: `${pair.outbound.destination_city}, ${pair.outbound.destination_state}`,
        return_origin: `${pair.return.origin_city}, ${pair.return.origin_state}`,
        return_destination: `${pair.return.destination_city}, ${pair.return.destination_state}`,
        total_miles:
          (pair.outbound.distance || 0) +
          (pair.return.distance || 0) +
          (pair.deadhead_miles || 0),
        deadhead_miles: pair.deadhead_miles || 0,
        total_revenue:
          (pair.outbound.rate_per_mile_est || 0) *
            (pair.outbound.distance || 0) +
          (pair.return.rate_per_mile_est || 0) * (pair.return.distance || 0),
        total_rate: pair.average_rate?.toFixed(2),
      });

      console.log("Saved load pair:", response.data);
      setSavedPairIds((prev) => new Set([...prev, pairId]));
    } catch (error) {
      console.error("Error saving load pair:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
        console.error("Error response headers:", error.response.headers);
      }
    } finally {
      setSavingPairId(null);
    }
  };

  const handleDeletePair = async (pair) => {
    const pairId = `${user.email}-${pair.outbound.id}-pair-${pair.return.id}`;
    setSavingPairId(pairId);

    try {
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        console.error("No authentication token found");
        return;
      }

      const entryId = `${user.email}-${pair.outbound.id}-pair-${pair.return.id}`;

      await axios.delete(`${API_URL}/loads/${encodeURIComponent(entryId)}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      setSavedPairIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(pairId);
        return newSet;
      });
    } catch (error) {
      console.error("Error deleting load pair:", error);
    } finally {
      setSavingPairId(null);
    }
  };

  const togglePairExpansion = (pairId) => {
    setExpandedPairIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pairId)) {
        newSet.delete(pairId);
      } else {
        newSet.add(pairId);
      }
      return newSet;
    });
  };

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "20px" }}>
      {/* Sort Dropdown */}
      <div
        style={{
          borderTop: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <div
          style={{
            position: "relative",
            minWidth: "200px",
          }}
        >
          <select
            value={sortOption}
            onChange={handleSortChange}
            style={{
              width: "100%",
              padding: "8px 12px",
              paddingRight: "30px",
              borderRadius: "4px",
              border: "1px solid var(--border)",
              backgroundColor: "var(--surface)",
              color: "var(--text)",
              cursor: "pointer",
              fontSize: "0.9rem",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
              boxShadow: "var(--shadow)",
            }}
          >
            <option
              value="score"
              style={{
                backgroundColor: "var(--surface)",
                color: "var(--text)",
              }}
            >
              Sort: Recommended
            </option>
            <option
              value="ratePerMile"
              style={{
                backgroundColor: "var(--surface)",
                color: "var(--text)",
              }}
            >
              Sort: Rate Per Mile
            </option>
            <option
              value="totalRevenue"
              style={{
                backgroundColor: "var(--surface)",
                color: "var(--text)",
              }}
            >
              Sort: Total Revenue
            </option>
            <option
              value="totalMiles"
              style={{
                backgroundColor: "var(--surface)",
                color: "var(--text)",
              }}
            >
              Sort: Total Miles
            </option>
            <option
              value="deadheadMiles"
              style={{
                backgroundColor: "var(--surface)",
                color: "var(--text)",
              }}
            >
              Sort: Deadhead Miles
            </option>
          </select>
          <div
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              color: "var(--text)",
            }}
          >
            <i className="fas fa-chevron-down"></i>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <p style={{ color: "var(--text)", marginBottom: "20px" }}>
        <i className="fas fa-info-circle" style={{ marginRight: "8px" }}></i>
        {sortedLoadPairs.length === 1
          ? `${sortedLoadPairs.length} load pair.`
          : `${sortedLoadPairs.length} load pairs.`}
      </p>

      {/* Load Pairs Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(650px, 1fr))",
          gap: "20px",
          marginTop: "20px",
        }}
      >
        {sortedLoadPairs.map((pair) => {
          const pairId = `${user.email}-${pair.outbound.id}-pair-${pair.return.id}`;
          const isExpanded = expandedPairIds.has(pairId);
          const isSaved = savedPairIds.has(pairId);

          return (
            <div
              key={pairId}
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "8px",
                border: "1px solid var(--border)",
                overflow: "hidden",
                cursor: "pointer",
                transition: "transform 0.2s ease",
                transform: isExpanded ? "scale(1.02)" : "none",
              }}
            >
              {/* Compact View */}
              <div
                onClick={() => togglePairExpansion(pairId)}
                style={{
                  padding: "15px",
                  borderBottom: isExpanded ? "1px solid var(--border)" : "none",
                }}
              >
                {/* Combined Metrics Banner */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "15px",
                    padding: "10px 15px",
                    backgroundColor: "var(--background)",
                    borderRadius: "8px",
                    marginBottom: "15px",
                  }}
                >
                  <div>
                    <div
                      style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}
                    >
                      Total Revenue
                    </div>
                    <div
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        color: "var(--success)",
                      }}
                    >
                      {formatCurrency(
                        pair.outbound.payment + pair.return.payment,
                      )}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}
                    >
                      Total Miles
                    </div>
                    <div style={{ fontSize: "1.1rem", fontWeight: "bold" }}>
                      {formatMiles(pair.total_miles)}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}
                    >
                      Deadhead
                    </div>
                    <div
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        color: "var(--danger)",
                      }}
                    >
                      {formatMiles(pair.deadhead_miles)}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}
                    >
                      Average Rate-per-Mile
                    </div>
                    <div
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        color: "var(--primary)",
                      }}
                    >
                      $
                      {(
                        (pair.outbound.payment + pair.return.payment) /
                        pair.total_miles
                      )?.toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Routes Side by Side */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "20px",
                    position: "relative",
                  }}
                >
                  {/* Outbound Route */}
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                        color: "var(--success)",
                      }}
                    >
                      <i className="fas fa-arrow-right"></i>
                      <span style={{ fontWeight: "bold" }}>Outbound</span>
                    </div>
                    <div style={{ fontSize: "1rem", marginBottom: "5px" }}>
                      {pair.outbound.origin_city}, {pair.outbound.origin_state}{" "}
                      → {pair.outbound.destination_city},{" "}
                      {pair.outbound.destination_state}
                    </div>
                    <div
                      style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}
                    >
                      {formatDate(pair.outbound.ship_date)} • $
                      {pair.outbound.rate_per_mile_est?.toFixed(2)}/mi
                    </div>
                  </div>

                  {/* Return Route */}
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                        color: "var(--primary)",
                      }}
                    >
                      <i className="fas fa-arrow-left"></i>
                      <span style={{ fontWeight: "bold" }}>Backhaul</span>
                    </div>
                    <div style={{ fontSize: "1rem", marginBottom: "5px" }}>
                      {capitalizeCity(pair.return.origin_city)},{" "}
                      {pair.return.origin_state} →{" "}
                      {capitalizeCity(pair.return.destination_city)},{" "}
                      {pair.return.destination_state}
                    </div>
                    <div
                      style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}
                    >
                      {formatDate(pair.return.ship_date)} • $
                      {pair.return.rate_per_mile_est?.toFixed(2)}/mi
                    </div>
                  </div>

                  {/* Expand/Collapse Icon */}
                  <div
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--text-muted)",
                    }}
                  >
                    <i
                      className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}
                    ></i>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div style={{ padding: "15px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "20px",
                      marginBottom: "15px",
                    }}
                  >
                    {/* Outbound Details */}
                    <div
                      style={{
                        padding: "15px",
                        backgroundColor: "var(--background)",
                        borderRadius: "8px",
                      }}
                    >
                      <h5
                        style={{
                          marginBottom: "15px",
                          color: "var(--success)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <i className="fas fa-arrow-right"></i>
                          Outbound Details
                        </span>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {getDataSourceBadge(pair.outbound.data_source, theme)}
                        </div>
                      </h5>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Weight:</strong>{" "}
                        {pair.outbound.weight
                          ? `${pair.outbound.weight.toLocaleString()} lbs`
                          : "N/A"}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Equipment:</strong>{" "}
                        {getTruckTypeLabel(pair.outbound.equipment_type)}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Length:</strong> {pair.outbound.length || "N/A"}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>DH:</strong>{" "}
                        {formatMiles(pair.outbound.origin_distance)}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Total Distance:</strong>{" "}
                        {pair.outbound.origin_distance
                          ? `${formatMiles(pair.outbound.distance + pair.outbound.origin_distance)}`
                          : `${formatMiles(pair.outbound.distance)}`}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Price:</strong>{" "}
                        {formatCurrency(pair.outbound.payment)}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Phone:</strong>{" "}
                        {pair.outbound.contact_phone || "N/A"}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Company Name:</strong>{" "}
                        {capitalizeCompanyName(pair.outbound.company_name) ||
                          "N/A"}
                      </div>
                      <div
                        style={{
                          marginBottom: "10px",
                          wordBreak: "break-all",
                          overflowWrap: "break-word",
                        }}
                      >
                        <strong>Company Email:</strong>{" "}
                        {pair.outbound.company_email || "N/A"}
                      </div>
                      <div>
                        <strong>ID:</strong> {pair.outbound.id}
                      </div>
                    </div>

                    {/* Return Details */}
                    <div
                      style={{
                        padding: "15px",
                        backgroundColor: "var(--background)",
                        borderRadius: "8px",
                      }}
                    >
                      <h5
                        style={{
                          marginBottom: "15px",
                          color: "var(--primary)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <i className="fas fa-arrow-left"></i>
                          Return Details
                        </span>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {getDataSourceBadge(pair.return.data_source, theme)}
                        </div>
                      </h5>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Weight:</strong>{" "}
                        {pair.return.weight
                          ? `${pair.return.weight.toLocaleString()} lbs`
                          : "N/A"}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Equipment:</strong>{" "}
                        {getTruckTypeLabel(pair.return.equipment_type)}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Length:</strong> {pair.return.length || "N/A"}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>DH:</strong>{" "}
                        {formatMiles(pair.return.origin_distance)}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Total Distance:</strong>{" "}
                        {pair.outbound.origin_distance
                          ? `${formatMiles(pair.return.distance + pair.return.origin_distance)}`
                          : `${formatMiles(pair.return.distance)}`}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Price:</strong>{" "}
                        {formatCurrency(pair.return.payment)}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Phone:</strong>{" "}
                        {pair.return.contact_phone || "N/A"}
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Company Name:</strong>{" "}
                        {capitalizeCompanyName(pair.return.company_name) ||
                          "N/A"}
                      </div>
                      <div
                        style={{
                          marginBottom: "10px",
                          wordBreak: "break-all",
                          overflowWrap: "break-word",
                        }}
                      >
                        <strong>Company Email:</strong>{" "}
                        {pair.return.company_email || "N/A"}
                      </div>
                      <div>
                        <strong>ID:</strong> {pair.return.id}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      marginTop: "15px",
                      borderTop: "1px solid var(--border)",
                      paddingTop: "15px",
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        isSaved ? handleDeletePair(pair) : handleSavePair(pair);
                      }}
                      style={{
                        backgroundColor: isSaved
                          ? "var(--danger)"
                          : "var(--primary)",
                        color: "var(--button-text)",
                        border: "none",
                        borderRadius: "4px",
                        padding: "8px 16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                      }}
                      disabled={savingPairId === pairId}
                    >
                      {savingPairId === pairId ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          {isSaved ? "Deleting..." : "Saving..."}
                        </>
                      ) : (
                        <>
                          <i
                            className={isSaved ? "fas fa-trash" : "fas fa-save"}
                          ></i>
                          {isSaved ? "Delete Pair" : "Save Pair"}
                        </>
                      )}
                    </button>
                    <a
                      href={buildPairGoogleMapsURL(
                        initialParams.origin_city,
                        initialParams.origin_state,
                        pair.outbound.origin_city,
                        pair.outbound.origin_state,
                        pair.outbound.destination_city,
                        pair.outbound.destination_state,
                        pair.return.origin_city,
                        pair.return.origin_state,
                        pair.return.destination_city,
                        pair.return.destination_state,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "8px 16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                        textDecoration: "none",
                        justifyContent: "center",
                      }}
                    >
                      <i className="fas fa-map-marked-alt"></i>
                      VIEW ROUTE
                    </a>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BackhaulResults;
